import { useCallback, useState } from 'react';

import type { PropertySetupValues } from '../propertySetup/types';

import { StayInputOverrides } from './otaParity/types';
import { StayInputsFormValues, toStayInputs } from './stayInputs';
import { useOtaRateShopOrchestration } from './useOtaRateShopOrchestration';

export const DEFAULT_DISCOUNT_PCT = 20;

// Dates are null while the picker holds a partial date.
export type RateShopInputs = {
  locationUrl: string;
  startDate: string | null;
  endDate: string | null;
  countryCode: string;
};

export type ScrapeDraft = StayInputsFormValues & {
  jobId: string | null;
  discountPct: number;
  rateShop: RateShopInputs | null;
};

// Combines stay inputs and the in-flight/completed rate-shop job into one Scrape session slice.
export function useScrapeSession(offerId: string | undefined) {
  const [stayInputs, setStayInputs] = useState<StayInputOverrides | null>(null);
  // Bumped on draft restore to remount the forms with the restored values.
  const [formKey, setFormKey] = useState(0);
  const [discountPct, setDiscountPct] = useState(DEFAULT_DISCOUNT_PCT);
  const [rateShopInputs, setRateShopInputs] = useState<RateShopInputs | null>(null);
  const [renderedOfferId, setRenderedOfferId] = useState(offerId);
  const orchestration = useOtaRateShopOrchestration(offerId);
  const { loadJob } = orchestration;

  // Cleared during render, not in an effect: StayInputsForm remounts on the same commit the offer
  // changes and reads its defaultValues then, so an effect would hand it the previous offer's
  // currency and FX rate and it would keep showing them.
  if (offerId !== renderedOfferId) {
    setRenderedOfferId(offerId);
    setStayInputs(null);
    setDiscountPct(DEFAULT_DISCOUNT_PCT);
    setRateShopInputs(null);
  }

  const loadDraft = useCallback(
    (
      { jobId, discountPct: draftDiscountPct, rateShop, ...values }: ScrapeDraft,
      propertySetup: PropertySetupValues | undefined,
    ) => {
      setStayInputs(toStayInputs(values, propertySetup));
      setDiscountPct(draftDiscountPct);
      setRateShopInputs(rateShop);
      setFormKey((key) => key + 1);
      if (jobId) {
        loadJob(jobId);
      }
    },
    [loadJob],
  );

  return {
    stayInputs,
    setStayInputs,
    discountPct,
    setDiscountPct,
    rateShopInputs,
    setRateShopInputs,
    offerId,
    formKey,
    loadDraft,
    ...orchestration,
  };
}
